/**
 * The unified production-record table (PROD-1279 stage-1 feedback, B3 / B4;
 * PROD-1441 layout, "3 · Kit outline", Abner 2026-10-08).
 *
 * A real `<table>` with the digital record's eleven columns in the printed
 * order, grouped Production (2) / Service (4) / Temps (3). Where the record VIEW
 * prints a number, this table puts the INPUT that produces it. Planned · Prepared
 * · Served · Leftover is the standard production-record order (2026-11-04):
 * Planned is the order, greyed and READ-ONLY (plain text, no box); Prepared is
 * what the meal had on hand, the one count the school enters before service.
 * The kitchen's own prepared count is no longer a column here.
 *
 * THE PAPER'S LAYOUT, TOLD APART BY TYPE AND SPACE. One level, one look:
 *   - the column groups are small caps labels over a hairline spanning their
 *     columns, the column labels plain and right-aligned over the numbers;
 *   - EVERY GRADE GROUP IS ITS OWN TABLE under a section title set directly
 *     above it (`Grade group K-8` and its meal pattern; `All grades` for rows
 *     with no band), one band or several alike (Abner 2026-10-08); no band
 *     row ever sits inside a table;
 *   - a SECTION (Main choices / Shared sides & milk / Condiments) is a 13px
 *     label on white with its caption, no strip; after the first section of a
 *     block, a hairline on top closes the tinted rows above it;
 *   - EVERY ITEM ROW sits on one light tint and only the titles (the column
 *     headers, a section) stay white (Abner 2026-10-08): the white rows are
 *     the breaks, the tinted ones the things counted;
 *   - an ITEM row is one counting row per item × band;
 *   - a SEALED KIT is one counting row with the ten cells, its title and tag on
 *     one line, and every recipe in it one line under the row, the name and the
 *     serving (`0.75 Cup`), running on across the empty value cells;
 *   - a grade group with main choices shows its REIMBURSABLE MEALS CLAIMED in
 *     a card of its own under its table (`ClaimedMealsCard`, which the phone
 *     review shows too): the mains' Served, Reimbursable and Non-reimbursable
 *     as the cells add up, each figure under its title.
 *
 * EVERY NUMERIC CELL IS A CONTROL on every counting row: `CountInput` (the
 * in-app keypad on a tablet) for the seven counts, the compact
 * `TempCheckpointInput` for the three readings. A pre-filled value says whose
 * number it is on hover and on focus (`hint`); an EMPTY box is a cell nobody
 * knows yet. A cell that has nowhere to save is a DISABLED box with a `title`.
 * A reading outside its holding band draws its box in the alert tone; Served
 * above Prepared draws it in the amber note tone. The
 * flow says why once, above the table.
 *
 * Temperatures are an ITEM attribute: the flow feeds every row of an item the
 * same value and writes the item when any row is edited. The three temp headers
 * carry a `title` saying so (plan A2).
 *
 * THE VALUE COLUMNS ARE FIXED AND COMPACT, the Item column takes the rest
 * (fixed table layout, the design's grid). Each value column fits its label
 * and its box, measured in Outfit: the widest label, `Received °F`, is 62px at
 * 12px. The Item column never drops under 20rem, so a kit's title and tag share
 * one line beside the mealtime rail (`Creamy Cheddar Mac kit` and its tag run
 * 273px; the cell's text box at the floor is 288px).
 *
 * DESKTOP-FIRST, deliberately: eleven columns scroll sideways on a narrow
 * screen rather than crush. THE ITEM COLUMN IS PINNED (2026-09-21): separate
 * borders drawn per cell, an opaque background under every pinned cell, and a
 * soft shadow once the scroller has moved (`data-scrolled`). The violations
 * footer under a row spans every column and is not pinned.
 *
 * Presentation only — every value and handler comes from the flow.
 */

import { cn } from '@ordo/ui/cn'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@ordo/ui/tooltip'
import { Fragment, type ReactNode, type UIEvent, useId } from 'react'

import type { TemperatureReadingWire } from '@/api/cafeteria-temperatures'
import { type CafeteriaModeTipProps, cafeteriaModeTipText } from '@/components/cafeteria/cafeteria-mode-tip'
import { CountInput } from '@/components/cafeteria/count-input'
import {
  NO_READING_LABEL,
  TEMP_CLASS_CHIP,
  TEMP_CLASS_LABEL,
  TEMPERATURE_RANGE_HINT,
  type TemperatureClass,
} from '@/components/cafeteria/temperature-table'
import { ClaimedMealsCard } from '@/components/production/spreadsheet/claimed-meals-card'
import { type TempCheckpointClass, TempCheckpointInput } from '@/components/tasks/temp-checkpoint-input'
import { formatTemperatureF, temperatureCellInvalid } from '@/lib/cafeteria/temperature-edit'
import { gradeGroupTitle } from '@/lib/production-records/spreadsheet-model'

/** Marks a cell a photo extraction filled at low confidence (PROD-1279 E5). */
export const RECORD_SPREADSHEET_CHECK_HINT = 'Check this value. The photo was hard to read.'

/** The three temperature headers' `title` (plan-05 D-B4.2, ASSUMED-2). */
export const TEMP_SCOPE_TITLE = 'One reading per item. Every row of an item shares it.'

export type RecordColumnKey =
  | 'ordered'
  | 'prepared'
  | 'received'
  | 'served'
  | 'reimbursable'
  | 'nonReimbursable'
  | 'leftover'
  | 'kitchenTemp'
  | 'receivedTemp'
  | 'servingTemp'

interface ColumnSpec {
  key: RecordColumnKey
  /** VERBATIM from `record-document-view.tsx` — the digital record's own header. */
  header: string
  /** Starts a column group (the phone's value lines split on it). */
  edge: boolean
  /** A temperature control. */
  temp?: boolean
  /** The fixed width: the label and the box, with the 17px right inset that sets the label over the digits. */
  width: string
}

/**
 * The value columns, in the printed order. The row model's `received` cell IS
 * the Prepared column (what the meal had on hand); its `prepared` cell (the
 * kitchen's count) is not shown.
 */
export const RECORD_SPREADSHEET_COLUMNS: readonly ColumnSpec[] = [
  { key: 'ordered', header: 'Planned', edge: false, width: 'w-[72px]' },
  { key: 'received', header: 'Prepared', edge: false, width: 'w-[72px]' },
  { key: 'served', header: 'Served', edge: true, width: 'w-[72px]' },
  { key: 'reimbursable', header: 'Reimb.', edge: false, width: 'w-[72px]' },
  // The one count label too wide for 72px: 62px of text and the inset.
  { key: 'nonReimbursable', header: 'Non-reimb.', edge: false, width: 'w-[80px]' },
  { key: 'leftover', header: 'Leftover', edge: false, width: 'w-[72px]' },
  { key: 'kitchenTemp', header: 'Kitchen °F', edge: true, temp: true, width: 'w-[84px]' },
  { key: 'receivedTemp', header: 'Received °F', edge: false, temp: true, width: 'w-[84px]' },
  { key: 'servingTemp', header: 'Serving °F', edge: false, temp: true, width: 'w-[84px]' },
]

/** The column groups over the headers, left to right; their spans add up to the ten value columns. */
const COLUMN_GROUPS: ReadonlyArray<{ label: string; span: number }> = [
  { label: 'Production', span: 2 },
  { label: 'Service', span: 4 },
  { label: 'Temps', span: 3 },
]

// ---------------------------------------------------------------------------
// Cells
// ---------------------------------------------------------------------------

interface CellBase {
  /** Rings the control amber and explains itself on hover; cleared on edit. */
  flag?: 'check'
  disabled?: boolean
  /** Why a disabled box takes no input (`This item is not on the production record.`). */
  title?: string
  /** Provenance mark for a pre-filled value, rendered beside the value on the phone's cards. */
  tip?: ReactNode
  /** The same provenance, which this table shows on hover and on focus of the box. */
  hint?: CafeteriaModeTipProps | null
  /** Draws the box in the alert tone: a reading outside its holding band. */
  alert?: boolean
  /** Draws the box in the amber note tone: Served above Prepared, which never blocks. */
  note?: boolean
  /**
   * The value is still Ordo's assumption (PROD-1511): the order, the delivery
   * plan, the kitchen's reading, or every meal ordered as Served. The phone's
   * tiles draw it dashed until someone types or scans over it; this table
   * ignores it.
   */
  prefilled?: boolean
  /** One word under a phone tile's number saying where it came from (`Full order`). */
  caption?: string
  ariaLabel: string
}

export interface RecordCountCell extends CellBase {
  kind: 'count'
  /** Shown as muted text with no input (Planned). */
  readOnly?: boolean
  /** `''` = nobody knows this number yet: the box is empty and the cell is omitted from every payload. */
  value: number | ''
  onValueChange: (next: string) => void
}

export interface RecordTempCell extends CellBase {
  kind: 'temp'
  /** False → `N/A — room temp`, no control. */
  offered: boolean
  value: number | null
  onChange: (next: number | null) => void
  /** What cafeteria mode logged here — the provenance shown ahead of `hint` / `tip`. */
  reading?: TemperatureReadingWire | null
}

export type RecordCell = RecordCountCell | RecordTempCell

/** One recipe inside a sealed kit: a count-less line under the kit's counting row (PROD-1441). */
export interface RecordSpreadsheetSubRowView {
  key: string
  name: string
  /** The serving: `0.75 cup` beside the name on the table, `Serving 0.75 cup` on the phone's own line; or empty. */
  legend: string
}

/**
 * A milk row's field-trip line (2026-11-04): the cartons of this milk the
 * school sent out with field-trip meals. One count, typed by the school; it
 * lowers the row's Leftover and never counts as Served.
 */
export interface RecordFieldTripView {
  /** `Sent on field trip`. */
  label: string
  /** `11 planned on field-trip meals`, or what the line is for. */
  legend: string
  /** Milk ordered on the Field Trip menu: read-only, under Planned. Null when none was. */
  planned: number | null
  /** `''` = none sent. */
  value: number | ''
  ariaLabel: string
  disabled?: boolean
  onValueChange: (next: string) => void
}

export interface RecordSpreadsheetRowView {
  key: string
  label: string
  /** The legend line: the portion (`2 each`), the kit note, vendor + ages, `Weekly milk delivery`. The band is the group header. */
  legend: string
  /** The title this table shows when it differs from `label` (a sealed kit's `Creamy Cheddar Mac kit`). */
  title?: string
  /** A tag beside the title (`Sealed kit · 2 recipes`). */
  badge?: string
  /** The line under the title on this table, in place of `legend` (a kit's `Counts are per kit`). */
  meta?: string
  temperatureClass: TemperatureClass | null
  /** Every one of the ten columns, always. */
  cells: Record<RecordColumnKey, RecordCell>
  /** Full-width node under the row (a row's violations + its Reset link). */
  footer?: ReactNode
  /** A sealed kit's recipes, rendered as count-less lines under this row. */
  subRows?: readonly RecordSpreadsheetSubRowView[]
  /** A milk row's field-trip line, rendered under the row with its one count. */
  fieldTrip?: RecordFieldTripView
}

export interface RecordSpreadsheetSectionView {
  key: string
  /** `Main choices`, `Shared sides & milk`, `Condiments`. */
  title: string
  /** `student selects one` under OVS, `not creditable` on condiments; null for none. */
  caption?: string | null
  rows: readonly RecordSpreadsheetRowView[]
}

/** The group's reimbursable meals claimed: the main choices' Served / Reimb / Non-reimb; null when the group has no main. */
export interface RecordSpreadsheetClaimedView {
  served: number | null
  reimbursable: number | null
  nonReimbursable: number | null
}

/** One grade group: its own titled table, and its claim card when it has main choices (PROD-1441). */
export interface RecordSpreadsheetGroupView {
  key: string
  /** The band (`K-8`), or `All grades`. */
  label: string
  /** The meal pattern beside the band (`PK-8 meal pattern`), or null. */
  pattern?: string | null
  sections: readonly RecordSpreadsheetSectionView[]
  claimed: RecordSpreadsheetClaimedView | null
}

export interface RecordSpreadsheetTableProps {
  groups: readonly RecordSpreadsheetGroupView[]
  emptyText?: string
}

/** The phone cards' word beside a kit recipe; the table shows none. */
export const IN_KIT_LABEL = 'in kit'

// ---------------------------------------------------------------------------
// Table
// ---------------------------------------------------------------------------

/** The hairline a counting row draws over itself (separate borders); the tbody strips it off the first row. */
const RULE = 'border-t border-border/60'
/** An Item cell: the title 14px down, the legend under it. */
const ITEM_TD = 'py-3.5 pl-5 pr-3 align-top'
/** A value cell: the box 12px down, right-aligned. */
const VALUE_TD = 'px-2 py-3 align-top'
/**
 * The table's own scroll height: the screen less the record's top bar and the
 * step's top padding, so the whole box fits once the page brings it up and
 * its header stays in view while the rows scroll under it.
 */
const TABLE_MAX_HEIGHT = 'max-h-[calc(100dvh-8rem)]'
/** The header stays put while a long mealtime scrolls under it. */
const STICKY = 'sticky z-20 bg-card'
/** The second header row sits under the group labels: 12px top, a 16px label, 6px, a 1px rule. */
const SECOND_HEADER_TOP = 'top-[35px]'
/** The Item column: pinned at the left over an opaque background. */
const PINNED = 'sticky left-0 bg-card'
/** Once the table has scrolled, the pinned column lifts off the cells sliding under it. */
const PINNED_LIFT = 'group-data-[scrolled=true]:shadow-[6px_0_8px_-6px_rgba(0,0,0,0.18)]'
/** Every item row's tint, a kit's recipe lines and a row's footer included; the titles stay white. */
const ROW_TINT = 'bg-muted/40'
/** The same tint on a pinned cell, painted OVER its opaque base so cells sliding under it stay hidden. */
const ROW_TINT_PINNED = 'bg-linear-to-r from-muted/40 to-muted/40'
/** The value columns' right inset, so a header and the claim line end where the digits below end: the cell's 8px, the box's 1px border and 8px padding. */
const VALUE_INSET = 'pr-[17px]'
/** The same edge for text inside a value cell, which pads 8px itself: the box's border and padding. */
const BOX_INSET = 'pr-[9px]'

/**
 * Flags the scroller once it has moved sideways, so the pinned column can show
 * its shadow. A data attribute the cells read, not React state: the whole
 * table would otherwise re-render on the first pixel of every scroll.
 */
function markScrolled(event: UIEvent<HTMLDivElement>): void {
  const scroller = event.currentTarget
  const next = scroller.scrollLeft > 0 ? 'true' : 'false'
  if (scroller.dataset['scrolled'] !== next) {
    scroller.dataset['scrolled'] = next
  }
}

export function RecordSpreadsheetTable({ groups, emptyText }: RecordSpreadsheetTableProps) {
  const rowCount = groups.reduce(
    (total, group) => total + group.sections.reduce((count, section) => count + section.rows.length, 0),
    0,
  )
  if (rowCount === 0) {
    return <p className="rounded-[10px] border px-3 py-8 text-center text-sm text-muted-foreground">{emptyText}</p>
  }
  // Every grade group alike, one band or several: its title, its own table, its claim card.
  return (
    <div className="space-y-10">
      {groups.map((group) => (
        <GradeGroupBlock key={group.key} group={group} />
      ))}
    </div>
  )
}

/**
 * One grade group: its section title directly above its own table, and the
 * reimbursable meals it claims in a card of its own under the table (only a
 * group with main choices claims any).
 */
function GradeGroupBlock({ group }: { group: RecordSpreadsheetGroupView }) {
  const titleId = useId()
  return (
    <section data-grade-group={group.label} aria-labelledby={titleId}>
      <div className="flex flex-wrap items-baseline gap-x-2.5 gap-y-1">
        <h3 id={titleId} className="text-lg font-semibold leading-6">
          {gradeGroupTitle(group.label)}
        </h3>
        {group.pattern ? <span className="text-[13px] text-muted-foreground">{group.pattern}</span> : null}
      </div>
      <div className="mt-3 overflow-hidden rounded-[10px] border">
        <GroupTable group={group} labelledBy={titleId} />
      </div>
      {group.claimed ? <ClaimedMealsCard claimed={group.claimed} className="mt-3" /> : null}
    </section>
  )
}

/** One grade group's table, named by its title: the column headers, then each section's label and its counting rows. */
function GroupTable({ group, labelledBy }: { group: RecordSpreadsheetGroupView; labelledBy: string }) {
  return (
    // Eleven columns do not fit a narrow screen; they scroll there rather than crush. The table also
    // scrolls DOWN inside this box, no taller than the screen: a sideways scroller is the box a sticky
    // header pins to, so in a box the page scrolled it rode away with the rows (Amana, 2026-11-03).
    <div className={cn('group overflow-auto bg-card', TABLE_MAX_HEIGHT)} data-scrolled="false" onScroll={markScrolled}>
      {/* Fixed layout: the widths come from the colgroup, never from the text, so a kit's recipe lines run
          on across the empty value cells without widening a column. 63.25rem = the Item column's 20rem
          floor plus the value columns' 692px. */}
      <table
        aria-labelledby={labelledBy}
        className="w-full min-w-[63.25rem] table-fixed border-separate border-spacing-0 text-[13px]">
        <colgroup>
          <col data-col="item" />
          {RECORD_SPREADSHEET_COLUMNS.map((column) => (
            <col key={column.key} data-col={column.key} className={column.width} />
          ))}
        </colgroup>
        <thead>
          <tr>
            {/* The corner: pinned on both axes. */}
            <th className={cn('top-0 z-30', PINNED, PINNED_LIFT)} />
            {COLUMN_GROUPS.map((columnGroup) => (
              <th
                key={columnGroup.label}
                colSpan={columnGroup.span}
                className={cn(STICKY, 'top-0 px-0 pb-0 pt-3 font-normal')}>
                <div className="mx-2 border-b border-border pb-1.5 text-left text-[11px] font-semibold uppercase leading-4 tracking-wider text-muted-foreground">
                  {columnGroup.label}
                </div>
              </th>
            ))}
          </tr>
          <tr>
            <th
              className={cn(
                'border-b pb-2.5 pl-5 pr-3 pt-2 text-left text-xs font-medium text-muted-foreground',
                PINNED,
                PINNED_LIFT,
                SECOND_HEADER_TOP,
                'z-30',
              )}>
              Item
            </th>
            {RECORD_SPREADSHEET_COLUMNS.map((column) => (
              <th
                key={column.key}
                title={column.temp ? TEMP_SCOPE_TITLE : undefined}
                className={cn(
                  'whitespace-nowrap border-b pb-2.5 pl-0 pt-2 text-right text-xs font-medium text-muted-foreground',
                  VALUE_INSET,
                  STICKY,
                  SECOND_HEADER_TOP,
                )}>
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="[&>tr:first-child>td]:border-t-0">
          {group.sections.map((section, sectionIndex) => (
            <Fragment key={section.key}>
              <SectionRow section={section} ruled={sectionIndex > 0} />
              {section.rows.map((row) => (
                <RowView key={row.key} row={row} />
              ))}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/**
 * A section's header row: the label and its caption on white, the rest of the
 * row empty. After a table's first section, its hairline closes the tinted
 * rows above.
 */
function SectionRow({ section, ruled }: { section: RecordSpreadsheetSectionView; ruled: boolean }) {
  const rule = ruled ? RULE : null
  return (
    <tr data-header-row="section" data-group-label={section.title}>
      <td className={cn(PINNED, PINNED_LIFT, 'z-10 px-5 pb-2 pt-5', rule)}>
        <div className="flex flex-wrap items-baseline gap-x-2.5">
          <span className="text-[13px] font-semibold leading-[18px] text-foreground">{section.title}</span>
          {section.caption ? <span className="text-xs text-muted-foreground">{section.caption}</span> : null}
        </div>
      </td>
      <td colSpan={RECORD_SPREADSHEET_COLUMNS.length} className={cn('pb-2 pt-5', rule)} />
    </tr>
  )
}

function RowView({ row }: { row: RecordSpreadsheetRowView }) {
  const lines = row.subRows ?? []
  const kit = lines.length > 0
  return (
    <>
      <tr data-row-kind={kit ? 'kit' : 'item'}>
        <td className={cn(RULE, ITEM_TD, PINNED, PINNED_LIFT, ROW_TINT_PINNED, 'z-10')}>
          <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
            <span data-row-title className="text-sm font-medium leading-5">
              {row.title ?? row.label}
            </span>
            {row.badge ? (
              <span
                data-row-badge
                className="shrink-0 rounded-sm bg-foreground/[0.06] px-1.5 text-[11px] font-medium leading-4 text-foreground/80">
                {row.badge}
              </span>
            ) : null}
          </div>
          <div className="mt-1 flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
            {row.temperatureClass ? (
              <span
                className={cn(
                  'shrink-0 rounded-sm px-1.5 text-[11px] font-medium leading-4',
                  TEMP_CLASS_CHIP[row.temperatureClass],
                )}>
                {TEMP_CLASS_LABEL[row.temperatureClass]}
              </span>
            ) : null}
            <span data-row-legend>{row.meta ?? row.legend}</span>
          </div>
        </td>
        {RECORD_SPREADSHEET_COLUMNS.map((column) => (
          <CellTd key={column.key} cell={row.cells[column.key]} temperatureClass={row.temperatureClass} />
        ))}
      </tr>
      {lines.map((line, index) => {
        const last = index === lines.length - 1
        return (
          <tr key={line.key} data-kit-sub-row={line.key}>
            {/* One line, never wrapped: past the Item column it runs on over the row's empty value cells.
                The layout is fixed, so the long line widens nothing. */}
            <td className={cn(PINNED, PINNED_LIFT, ROW_TINT_PINNED, 'z-10 py-[7px] pl-7 pr-3', last && 'pb-[15px]')}>
              <div className="flex items-center gap-2.5 whitespace-nowrap">
                <svg
                  aria-hidden="true"
                  className="h-4 w-3 shrink-0 text-muted-foreground/50"
                  viewBox="0 0 12 16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5">
                  <path d="M1 0v9a3 3 0 0 0 3 3h8" />
                </svg>
                <span className="text-[13px] leading-[18px] text-foreground/80">{line.name}</span>
                {line.legend ? <span className="text-xs text-muted-foreground">{line.legend}</span> : null}
              </div>
            </td>
            <td colSpan={RECORD_SPREADSHEET_COLUMNS.length} className={ROW_TINT} />
          </tr>
        )
      })}
      {row.fieldTrip ? <FieldTripRow line={row.fieldTrip} /> : null}
      {row.footer ? (
        <tr>
          <td colSpan={RECORD_SPREADSHEET_COLUMNS.length + 1} className={cn('px-5 pb-2', ROW_TINT)}>
            {row.footer}
          </td>
        </tr>
      ) : null}
    </>
  )
}

/**
 * A milk row's field-trip line: the trip's planned milk under Planned, the
 * cartons sent under Served (they went out, but are not the cafeteria's
 * Served), the rest of the row empty.
 */
function FieldTripRow({ line }: { line: RecordFieldTripView }) {
  const after = RECORD_SPREADSHEET_COLUMNS.length - 3
  return (
    <tr data-field-trip-row>
      <td className={cn(PINNED, PINNED_LIFT, ROW_TINT_PINNED, 'z-10 py-[7px] pb-[15px] pl-7 pr-3')}>
        <div className="flex items-center gap-2.5">
          <svg
            aria-hidden="true"
            className="h-4 w-3 shrink-0 text-muted-foreground/50"
            viewBox="0 0 12 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5">
            <path d="M1 0v9a3 3 0 0 0 3 3h8" />
          </svg>
          <div className="min-w-0">
            <div className="text-[13px] font-medium leading-[18px] text-foreground/80">{line.label}</div>
            <div className="text-xs text-muted-foreground">{line.legend}</div>
          </div>
        </div>
      </td>
      <td className={cn('px-2 py-[7px] align-top', ROW_TINT)}>
        <div
          data-read-only
          className={cn('flex h-8 items-center justify-end tabular-nums text-muted-foreground', BOX_INSET)}>
          {line.planned === null ? '' : line.planned}
        </div>
      </td>
      <td className={ROW_TINT} />
      <td className={cn('px-2 py-[7px] align-top', ROW_TINT)}>
        <div className="flex justify-end">
          <CountInput
            className="h-8 w-14 px-2 text-right"
            aria-label={line.ariaLabel}
            value={line.value}
            disabled={line.disabled}
            onValueChange={line.onValueChange}
          />
        </div>
      </td>
      <td colSpan={after} className={ROW_TINT} />
    </tr>
  )
}

function CellTd({ cell, temperatureClass }: { cell: RecordCell; temperatureClass: TemperatureClass | null }) {
  const className = cn(RULE, VALUE_TD, ROW_TINT)
  if (cell.kind === 'count' && cell.readOnly) {
    return (
      <td className={className} title={cell.title}>
        <Hint hint={cell.hint}>
          <div
            data-read-only
            aria-label={cell.ariaLabel}
            className={cn('flex h-8 items-center justify-end tabular-nums text-muted-foreground', BOX_INSET)}>
            {cell.value === '' ? '—' : cell.value}
          </div>
        </Hint>
      </td>
    )
  }
  if (cell.kind === 'count') {
    return (
      <td className={className} title={cell.title}>
        <div className="flex justify-end">
          <Hint hint={cell.hint}>
            <FlagWrap flag={cell.flag}>
              <CountInput
                className={cn(
                  'h-8 w-14 px-2 text-right',
                  cell.alert && 'border-destructive/50 bg-destructive/5 text-destructive',
                  cell.note &&
                    'border-amber-400 bg-amber-50 text-amber-900 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-200',
                )}
                aria-label={cell.ariaLabel}
                aria-invalid={cell.alert || undefined}
                value={cell.value}
                disabled={cell.disabled}
                onValueChange={cell.onValueChange}
              />
            </FlagWrap>
          </Hint>
        </div>
      </td>
    )
  }
  return (
    <td className={className} title={cell.title}>
      <TempCellView cell={cell} temperatureClass={temperatureClass} />
    </td>
  )
}

/**
 * Whose number a pre-filled box holds, on hover and on focus of the box itself
 * (the info icon beside every box is gone). The wrapper carries the sentence as
 * data, so the source stays checkable without opening the tooltip.
 */
function Hint({ hint, children }: { hint?: CafeteriaModeTipProps | null; children: ReactNode }) {
  if (!hint) {
    return <>{children}</>
  }
  const text = cafeteriaModeTipText(hint)
  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex" data-tip-kind={hint.kind} data-tip-text={text}>
            {children}
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs whitespace-normal">
          {text}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

/** The amber low-confidence ring, or a plain wrapper. Presentation only. */
function FlagWrap({ flag, children }: { flag?: 'check'; children: ReactNode }) {
  return flag === 'check' ? (
    <div className="rounded-md ring-2 ring-amber-500/60" title={RECORD_SPREADSHEET_CHECK_HINT}>
      {children}
    </div>
  ) : (
    <>{children}</>
  )
}

function TempCellView({ cell, temperatureClass }: { cell: RecordTempCell; temperatureClass: TemperatureClass | null }) {
  if (!cell.offered) {
    return (
      <div className={cn('flex h-8 items-center justify-end text-right text-xs text-muted-foreground', BOX_INSET)}>
        {NO_READING_LABEL}
      </div>
    )
  }
  // A reading cafeteria mode logged outranks the row's own provenance.
  const hint: CafeteriaModeTipProps | null = cell.reading
    ? {
        kind: 'logged',
        label: formatTemperatureF(cell.reading.value),
        loggedAt: cell.reading.loggedAt,
        loggedByName: cell.reading.loggedByName,
      }
    : (cell.hint ?? null)
  return (
    <div className="flex flex-col items-end gap-1">
      <Hint hint={hint}>
        <FlagWrap flag={cell.flag}>
          <TempCheckpointInput
            variant="compact"
            alert={cell.alert}
            value={cell.value}
            onChange={cell.onChange}
            ariaLabel={cell.ariaLabel}
            temperatureClass={tempCheckpointClass(temperatureClass)}
            disabled={cell.disabled}
          />
        </FlagWrap>
      </Hint>
      {temperatureCellInvalid(cell.value) ? (
        <p className="text-right text-xs text-destructive">{TEMPERATURE_RANGE_HINT}</p>
      ) : null}
    </div>
  )
}

/** The class maps `TemperatureTable` / `RecordEntryGrid` use. */
function tempCheckpointClass(temperatureClass: TemperatureClass | null): TempCheckpointClass {
  switch (temperatureClass) {
    case 'HOT':
      return 'hot'
    case 'CHILLED':
      return 'chilled'
    case 'AMBIENT':
      return 'ambient'
    default:
      return null
  }
}
